import { property, subclass } from '@arcgis/core/core/accessorSupport/decorators';

import { messageBundle, storeNode, tsx } from '@arcgis/core/widgets/support/widget';

import Widget from '@arcgis/core/widgets/Widget';
import { VNode } from 'src/interfaces/interfaces';

import "./AppTile/styles/AppTile.scss";
import AppTypeInfoModal from './AppTypeInfoModal';
import t9nAppMessages from "../../t9n/App/App.json";
import ContentAreaViewModel from '../ui/ContentArea/ContentAreaViewModel';
import AppTypeTile from './AppTypeTile';
import PublishForm from './PublishForm';
import IFrameAppPreview from './IFrameAppPreview';

import CircleDownCarotSVG from "../../../public/assets/images/Info_icon.svg";
import GenericMyAppsImg from "../../../public/assets/images/GenericMyAppImg.png";
import { keyboardAccessibleHelper } from '../utils/appUtils';
import { whenDefinedOnce } from '@arcgis/core/core/watchUtils';
import { focusElement } from '../utils/focusUtils';
import DeleteModal from './DeleteModal';
import AppStudioModal from './AppStudioModal';
import { applyShadowStyle } from '../utils/utils';

import { SHADOW_STYLE_CARD, SHADOW_STYLE_DROPDOWN } from '../utils/shadowStyles';
import { EAppTemplateType } from 'templates-common-library/baseClasses/CompatibilityChecker';
import PortalQueryParams from '@arcgis/core/portal/PortalQueryParams';


export interface AppTileProperties extends __esri.WidgetProperties {
  appInfo: __esri.PortalItem;
  portal: __esri.Portal;
  contentAreaVM: ContentAreaViewModel;
}

export interface AppData {
  folderId: string;
  source: string;
  values: {
    draft?: any,
    [fieldName: string]: any
  }
}

export interface ddButtonDef {
  icon: string;
  onClickFunc: Function;
  label: string;
}

@subclass('Landing.widgets.AppTile')
export default class AppTile extends Widget {
  @property()
  identifier: "AppTile" = "AppTile";

  @property()
  appInfo: __esri.PortalItem;

  @property()
  portal: __esri.Portal;

  @property()
  contentAreaVM: ContentAreaViewModel;

  @property()
  resourceItem: __esri.PortalItem | __esri.PortalGroup | number = undefined; // (undefined => null) || (undefined => __esri.PortalItem) --- This is how we know loading is done

  @property()
  appTypeTemplate: AppTypeTile;

  @property()
  infoModal: AppTypeInfoModal;

  @property()
  saveCopyModal: PublishForm;

  @property()
  chooseFromExistingModal: PublishForm;

  @property()
  iFramePreviewModal: IFrameAppPreview;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof t9nAppMessages;

  @property()
  dropDownMenuButton: HTMLCalciteButtonElement;


  @property()
  appTileDropDownOptions: ddButtonDef[];

  @property()
  _configureBtn: ddButtonDef;
  @property()
  _chooseWithExistingBtn: ddButtonDef;
  @property()
  _copyBtn: ddButtonDef;
  @property()
  _deleteBtn: ddButtonDef;
  @property()
  _deleteModal: DeleteModal;
  @property()
  _orgUrl: string;
  @property()
  _previewBtn: ddButtonDef;
  @property()
  _viewAppBtn: ddButtonDef;
  @property()
  _viewDetailsBtn: ddButtonDef;
  @property()
  _appData: AppData;
  @property()
  _appStudioModal: AppStudioModal;

  /** Saved active element when a modal is launched */
  @property()
  _activeElement: HTMLElement;

  constructor(properties?: AppTileProperties) {
    super(properties);
  }

  postInitialize() {
    this._populateDDOptions();

    whenDefinedOnce(this.contentAreaVM.publishAppVM, "orgUrl", (orgUrl) => {
      this._orgUrl = orgUrl;
      this._initDeleteModal();
      this._initAppStudioModal();
    });


    this.appInfo.fetchData("json").then((data: AppData) => {
      this._appData = data;

      this._findResourceData(data);

      const appTypeItemId: string = data.source;

      this.appTypeTemplate = this?.contentAreaVM?.browseAllTiles.find((appTemplate: AppTypeTile) => {
        return appTemplate?.appTypeInfo?.appTypeId === appTypeItemId;
      });
    });
  }

  render() {
    const { title, modified } = this.appInfo;
    const {
      modifiedMsg,
    } = this.appMessages.myApps;

    return (
      <div class="app-tile" id={this.appInfo.id}>
        <calcite-card
          afterCreate={(card) => applyShadowStyle(SHADOW_STYLE_CARD, card)}
          class="app-card"
        >

          {this._renderImg()}

          <h2 slot="title" class="app_title" title={title}>{title}</h2>

          {this._renderAppTemplateInfo()}

          <div class="app-metadata__map-title">
            {this._resourceInfoDisplay()}
          </div>

          <div class="app-metadata__bottom-row">
            <div class="app-metadata__modified-date">
              <calcite-icon icon="map" scale="s"></calcite-icon> {modifiedMsg} {modified.toLocaleString().split(",")[0]}
            </div>
          </div>

          {this._renderDropdownMenu()}
          {this._renderFooter()}
        </calcite-card>

        { this.infoModal?.render()}
        { this.saveCopyModal?.render()}
        { this.chooseFromExistingModal?.render()}
        { this.iFramePreviewModal?.render()}
        { this._deleteModal?.render()}
        { this._appStudioModal?.render()}
      </div>
    )
  }

  private _initDeleteModal() {
    this._deleteModal = new DeleteModal({
      appInfo: this.appInfo,
      orgUrl: this._orgUrl,
      contentAreaVM: this.contentAreaVM
    });
  }

  private _initAppStudioModal() {
    this._appStudioModal = new AppStudioModal({
      appInfo: this.appInfo,
      orgUrl: this._orgUrl,
      contentAreaVM: this.contentAreaVM
    });
  }

  private _renderImg(): VNode {
    const {
      viewApp
    } = this.appMessages.myApps;
    return (
      <div
        class="app-tile__img-container"
        slot="thumbnail"
        data-overlay-desc={viewApp}
        onclick={this._openApp.bind(this)}
        onkeydown={keyboardAccessibleHelper.bind(
          this,
          this._openApp.bind(this)
        )}
        tabindex="0"
        title={viewApp}
      >
        <calcite-icon
          class="app-tile__img-launch"
          icon="launch"
          scale="m">
        </calcite-icon>
        <img
          class="app-tile__img"
          alt=""
          src={(this?.resourceItem as any)?.thumbnailUrl != null ? (this?.resourceItem as any)?.thumbnailUrl : GenericMyAppsImg}
        />
      </div>
    )
  }

  private _renderAppTemplateInfo(): VNode {
    const { appTemplate } = this.appMessages.myApps;
    return (
      <div class="app-metadata__template">
        <strong>{appTemplate}:&nbsp;</strong>{this?.appTypeTemplate?.appTypeInfo?.appTypeTitle}
      &nbsp;
        <img
          src={CircleDownCarotSVG}
          tabindex="0"
          onclick={this._initializeInfoModal.bind(this)}
          onkeydown={keyboardAccessibleHelper.bind(
            this,
            this._initializeInfoModal.bind(this)
          )}
          class="apptile-down-chevron-svg"
          alt="" />
      </div>
    )
  }

  private _populateDDOptions() {
    const {
      preview,
      viewApp,
      ddButtonsOptions
    } = this.appMessages.myApps;
    const { choose, configure, saveCopy,
      viewDetails, deleteLabel } = ddButtonsOptions;

    this._chooseWithExistingBtn = {
      icon: "applications", label: choose,
      onClickFunc: () => {
        this._initializeChooseWithExisting();
      }
    };
    this._configureBtn = {
      icon: "gear", label: configure,
      onClickFunc: () => {
        window.open(`${this._orgUrl}/apps/configure-template/index.html?appid=${this.appInfo.id}`);
      }
    };
    this._copyBtn = {
      icon: "copy", label: saveCopy,
      onClickFunc: () => {
        this._initializeSaveCopy();
      }
    };
    this._viewDetailsBtn = {
      icon: "infographic", label: viewDetails,
      onClickFunc: () => {
        window.open(`${this._orgUrl}/home/item.html?id=${this.appInfo.id}`);
      }
    };
    this._deleteBtn = {
      icon: "trash", label: deleteLabel,
      onClickFunc: () => {
        this._deleteModal.showModal();
      }
    };
    this._previewBtn = {
      icon: "camera-flash-on", label: preview,
      onClickFunc: () => {
        this._initializePreview();
      }
    };
    this._viewAppBtn = {
      icon: "launch", label: viewApp,
      onClickFunc: () => {
        this._openApp();
      }
    }

    this.appTileDropDownOptions = this._isNoWebmapWorkflow() ?
      [this._chooseWithExistingBtn, this._copyBtn, this._viewAppBtn, this._viewDetailsBtn, this._deleteBtn] :
      [this._chooseWithExistingBtn, this._previewBtn, this._copyBtn, this._viewAppBtn, this._viewDetailsBtn, this._deleteBtn];

    if (this._shouldAddAppStudioIntegration()) {
      this.appTileDropDownOptions.push({
        icon: "app-run",
        label: "ArcGIS AppStudio",
        onClickFunc: () => {
          this._appStudioModal.showModal();
        }
      });
    }
  }

  private _shouldAddAppStudioIntegration(): boolean {
    const templateType = this.appInfo.url;
    return templateType.includes(EAppTemplateType.AttachmentViewer)
      || templateType.includes(EAppTemplateType.Nearby)
      || templateType.includes(EAppTemplateType.ZoneLookup);
  }

  private _renderDropdownMenu(): VNode {
    return (
      <calcite-dropdown
        class="app-tile__dropdown-options"
        slot="footer-leading"
        placement="bottom-leading"
        type="click"
        scale="m"
        width="m"
      >
        <calcite-button
          bind={this}
          afterCreate={storeNode}
          data-node-ref="dropDownMenuButton"
          slot="dropdown-trigger"
          scale="l"
          appearance="transparent"
          color="blue"
          icon-start="handleVertical"
          alignment="center"
          width="auto"
        ></calcite-button>
        <calcite-dropdown-group
          role="menu"
          selection-mode="none"
        >
          {this._renderDropDownOptions()}
        </calcite-dropdown-group>
      </calcite-dropdown>
    );
  }

  _renderDropDownOptions(): VNode[] {
    return (
      this?.appTileDropDownOptions?.map((option: ddButtonDef) => {
        const { icon, label, onClickFunc } = option;
        return (
          <calcite-dropdown-item
            afterCreate={(ddItem) => applyShadowStyle(SHADOW_STYLE_DROPDOWN, ddItem)}
            title={label}
            icon-start={icon}
            class="appTileDropDownLinks"
            onclick={(e) => {
              e.preventDefault();
              onClickFunc.call(this, e);
            }}
          >
            {label}
          </calcite-dropdown-item>
        )
      })
    )
  }

  private async _queryForPortalItem(itemId: string): Promise<__esri.PortalItem | null> {
    if (itemId == null) { return; }
    const q: __esri.PortalQueryParams = {} as any;
    q.query = `id:"${itemId}"`;
    const res: __esri.PortalQueryResult = await this.portal.queryItems(q);
    if (res.results.length > 0) {
      return res.results[0];
    } else {
      return null
    }
  }

  private _initializeInfoModal() {
    this._saveActiveElem();
    const { appTypeTitle, thumbnailUrl, appTypeUrlFragment } = this?.appTypeTemplate?.appTypeInfo;
    this.infoModal = new AppTypeInfoModal({
      picUrl: thumbnailUrl,
      appTypeTitle,
      appTypeUrlFragment,
      sendFocusOnClose: this._focusPriorActiveElem()
    });
  }

  private _initializeSaveCopy() {
    this._saveActiveElem();
    const { title, successMsg, failureMsg } = this.appMessages.myApps.saveCopyAlert;
    const { appTypeInfo, publishAppVM } = this?.appTypeTemplate;
    this.saveCopyModal = new PublishForm({
      ui: "modal",
      mode: "copyApp",
      appTypeInfo,
      existingAppItem: this.appInfo,

      publishAppVM,
      feedbackFunction: (msg: string, success: boolean, appId?: string) => {
        this.contentAreaVM.showAlert(
          title,
          success ? `${successMsg}` : `${failureMsg}: ${msg}`,
          success ? "success" : "error",
          appId
        );
        this.contentAreaVM.myAppsDataVM.queryAllMyApps();
      },
      sendFocusOnClose: this._focusPriorActiveElem()
    });
  }

  private _initializeChooseWithExisting() {
    this._saveActiveElem();
    const { appTypeInfo, publishAppVM, resourceInfo } = this?.appTypeTemplate;
    this.chooseFromExistingModal = new PublishForm({
      ui: "modal",
      mode: "fromExistingApp",
      appTypeInfo,
      existingAppItem: this.appInfo,
      resourceInfo,

      publishAppVM,
      feedbackFunction: (msg: string, success: boolean, appId?: string) => {
        this.contentAreaVM.myAppsDataVM.queryAllMyApps();
      },
      sendFocusOnClose: this._focusPriorActiveElem()
    });
  }

  private _initializePreview(): void {
    this._saveActiveElem();
    const { appTypeInfo, publishAppVM, resourceInfo } = this?.appTypeTemplate;
    this.iFramePreviewModal = new IFrameAppPreview({
      appTypeInfo,
      resourceInfo,
      publishAppVM,
      previewType: "mapPreview",
      existingApp: this.appInfo,
      sendFocusOnClose: this._focusPriorActiveElem()
    });
  }

  private _renderFooter() {
    return (
      <div slot="footer-trailing" class="bottom-buttons">
        {
          // Leaving in this logic for differing buttons between different workflows 
          // (in case we change back to this in the future)
          this._isNoWebmapWorkflow()
            ? this._renderFooterButtons(this._configureBtn)
            : this._renderFooterButtons(this._configureBtn)
        }
      </div>
    )
  }

  private _isNoWebmapWorkflow(): boolean {
    return this.contentAreaVM.itemLoaderVM.loadedItemType === "none";
  }

  private _renderFooterButtons(primary: ddButtonDef, secondary?: ddButtonDef): VNode[] {
    return [
      secondary != null ?
        <calcite-button
          scale="m"
          class="footer-half"
          icon-start={secondary.icon}
          appearance="outline"
          alignment="center"
          width="full"
          onclick={(e) => {
            e.preventDefault();
            secondary.onClickFunc.call(this, e);
          }}
        >
          {secondary.label}
        </calcite-button>
        : null,
      <calcite-button
        scale="m"
        class={secondary != null ? "footer-half" : "footer-full"}
        icon-start={primary.icon}
        alignment="center"
        width="full"
        onclick={(e) => {
          e.preventDefault();
          primary.onClickFunc.call(this, e);
        }}
      >
        {primary.label}
      </calcite-button>
    ]
  }

  private _openApp() {
    window.open(this.appInfo.url, "_blank");
  }

  private _saveActiveElem() {
    this._activeElement = document.activeElement as HTMLElement;
  }

  private _focusPriorActiveElem(): Function {
    return focusElement.bind(this, this._activeElement);
  }

  private _findResourceData(data: AppData) {
    const { url } = this.appInfo;
    if (url.indexOf(EAppTemplateType.Portfolio) !== -1) {
      const itemCollection = data?.values?.itemCollection || data?.values?.draft?.itemCollection;
      this.resourceItem = itemCollection?.length;

    } else if (url.indexOf(EAppTemplateType.CategoryGallery) !== -1) {
      const groupId = data?.values?.group || data?.values?.draft?.group;
      const queryParams = new PortalQueryParams({
        query: `id:"${groupId}"`,
        disableExtraQuery: true, // stops orgId from being appended to query
        sortField: "modified",
        sortOrder: "desc",
        num: 100
      } as any);

      this.portal.queryGroups(queryParams).then((groupResult) => {
        const groupRes = groupResult.results;
        this.resourceItem = groupRes?.[0];
      });

    } else {
      const resourceId: string =
        data?.values?.webmap || data?.values?.draft?.webmap ||
        data?.values?.webscene || data?.values?.draft?.webscene;

      // Query for App's Webmap
      this._queryForPortalItem(resourceId).then((item: __esri.PortalItem) => {
        this.resourceItem = item;
      });
    }
  }

  private _resourceInfoDisplay(): VNode {
    const {
      items
    } = this.appMessages.myApps;

    const { url } = this.appInfo;
    if (url.indexOf(EAppTemplateType.Portfolio) !== -1) {
      const itemCollectionLength = (this.resourceItem as number);
      return <span>
        <strong>{items}:</strong> {itemCollectionLength != null ? itemCollectionLength : 0}
      </span>
    } else if (url.indexOf(EAppTemplateType.CategoryGallery) !== -1) {
      return this._renderGroupInfo();
    } else {
      return this._renderWebMapOrSceneInfo();
    }
  }

  private _renderGroupInfo(): VNode {
    const {
      group,
      noResource
    } = this.appMessages.myApps;
    const groupTitle = (this?.resourceItem as __esri.PortalGroup)?.title;
    const id = (this?.resourceItem as __esri.PortalGroup)?.id;
    if (groupTitle != null) { // The Title exists, meaning the group exists, so we can link out to it
      return (
        <span>
          <strong>{group}:</strong>
          &nbsp;
          <calcite-link icon-end="launch" href={`${this._orgUrl}/home/group.html?id=${id}`} target="_blank">
            {groupTitle}
          </calcite-link>
        </span>
      );
    } else { // group has not been picked yet in this app
      return (
        <span>
          <strong>
            {group}:
          </strong>
          &nbsp;
          {noResource}
        </span>
      );
    }
  }

  private _renderWebMapOrSceneInfo(): VNode {
    const {
      map,
      scene,
      noResource
    } = this.appMessages.myApps;
    const { type, title, id } = (this?.resourceItem as __esri.PortalItem) || {};
    const resourceLabel: string = type === "Web Scene" ? scene : map;
    const urlSection: string = type === "Web Scene" ? "/home/webscene/viewer.html?webscene=" : "/apps/mapviewer/index.html?webmap=";
    if (title != null) { // The Title exists, meaning the webmap/webscene exists, so we can link out to it
      return (
        <span>
          <strong>
            {resourceLabel}:
          </strong>
          &nbsp;
          <calcite-link icon-end="launch" href={`${this._orgUrl}${urlSection}${id}`} target="_blank">
            {title}
          </calcite-link>
        </span>
      );
    } else { // webmap/webscene has not been picked yet in this app
      return (
        <span>
          <strong>
            {resourceLabel}:
          </strong>
          &nbsp;
          {noResource}
        </span>
      );
    }
  }
}
